<template>
  <el-dialog
    v-loading="loading"
    class="recordDialog"
    :title="$t('records.dialog.title')"
    :visible.sync="visible"
    :before-close="handleClose"
    append-to-body
    :width="width"
  >
    <template v-if="!loading && record">
      <p>{{ $t('records.dialog.text')}}<span class="primaryColor">{{ record.state_label }}</span></p>

      <el-radio
        v-if="!isWaitingPrice"
        v-model="radio"
        class="recordDialog__radio"
        :label="true"
      >{{$t(`records.dialog.radioTitle.${record.state}`)}}</el-radio>

      <el-row
        v-if="isWaitingPrice"
        :gutter="56"
      >
        <el-col :lg="12">
          <blockquote class="recordDialog__infos">
            <p>{{ $t('records.dialog.details1')}}
              <span class="primaryColor">{{record.unit.unit_type.commercial_title}}</span>
            </p>
            <p>{{ $t('records.dialog.details2')}}
              <span class="primaryColor">{{record.unit.number}}</span>
            </p>
            <p>{{ $t('records.dialog.details3')}}
              <span class="primaryColor">{{record.unit.floor_number.title}}</span>
            </p>
            <p>{{ $t('records.dialog.details4')}}
              <span class="primaryColor">{{record.unit.surface}}&nbsp;{{$t('ui.surfaceDisplay.squareFoot')}}</span>
            </p>
            <p>{{ $t('records.dialog.details7')}}
              <span class="primaryColor">{{record.rent_amount_without_charges}}</span>
            </p>
            <p>{{ $t('records.dialog.details8')}}
              <span class="primaryColor">{{record.annex_rent_amount}}</span>
            </p>
            <p v-if="record.charges_amount">{{ $t('records.dialog.details9')}}
              <span class="primaryColor">{{record.charges_amount}}</span>
            </p>
            <p>{{ $t('records.dialog.details10')}}
              <span class="primaryColor">{{record.deposit_amount}}</span>
            </p>
            <p v-if="record.packages_amount">{{ $t('records.dialog.details11')}}
              <span class="primaryColor">{{record.packages_amount}}</span>
            </p>
            <p><b>{{ $t('records.dialog.details5')}}</b>
              <span class="primaryColor"><b>{{record.rent_amount}}</b></span>
            </p>
            <p v-if="record.agreement_given_at">
              <b>{{ $t('records.dialog.details6') }}</b>
              <span class="primaryColor"><b>{{ $moment(record.agreement_given_at).format('DD MMMM YYYY') }}</b></span></p>
          </blockquote>
        </el-col>
        <el-col
          v-if="optionalChoices && optionalChoices.length"
          :lg="12"
        >
          <div class="recordDialog__optional">
            <p><b>{{ $t('records.dialog.optional_services') }}</b></p>
            <p class="recordDialog__optional_text">{{ $t('records.dialog.optional_services_text') }}</p>
            <div
              v-for="optionalChoice in optionalChoices"
              :key="optionalChoice.id"
              class="recordDialog__optional_checkbox"
            >
              <el-checkbox
                v-model="checkboxChoice[optionalChoice.id]"
                :title="optionalChoice.description"
              >
                {{ optionalChoice.title }} : <span class="primaryColor">{{ optionalChoice.price }}</span>
              </el-checkbox>
              <el-popover
                v-if="optionalChoice.description"
                trigger="click"
                width="250"
                :content="optionalChoice.description"
              >
                <el-button slot="reference">
                  <md-icon name="info" />
                </el-button>
              </el-popover>
            </div>
          </div>
        </el-col>
      </el-row>

      <el-radio-group
        v-if="isWaitingPrice"
        v-model="radio"
        class="recordDialog__radioGroup"
        @change="onRadioChange()"
      >
        <el-radio :label="true">
          {{$t('records.dialog.radioTitle.radio_yes')}}
        </el-radio>
        <el-radio :label="false">
          {{$t('records.dialog.radioTitle.radio_no')}}
          <el-select
            v-model="radioChoice"
            class="recordDialog__select"
            :placeholder="$t('records.dialog.select')"
            :disabled="radio === true"
          >
            <el-option
              v-for="choice in choices"
              :key="choice"
              :label="$t(`records.dialog.choices.${choice}`)"
              :value="choice"
            >
              {{ $t(`records.dialog.choices.${choice}`) }}
            </el-option>
          </el-select>
        </el-radio>
      </el-radio-group>

      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button
          :loading="loading"
          :disabled="radio === null"
          @click="handleUpdate"
        >{{ $t(`records.dialog.updateButton.${record.state}`)}}</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
export default {
  name: 'RecordDialog',
  props: {
    recordId: {
      type: Number,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    width: '50%',
    windowWidth: 0,
    loading: false,
    record: null,
    radio: null,
    radioChoice: null,
    checkboxChoice: {},
    mapStates: {
      'processing_missing_documents': 'processing_added_documents',
      'processing_missing_guarantors': 'processing_added_guarantors',
      'granted_waiting_price_acceptation': {
        true: 'granted_accepted',
        false: 'refused_accommodation_declined'
      }
    },
    choices: ['too_expensive', 'found_another_offer'],
    optionalChoices: null
  }),
  computed: {
    isWaitingPrice() {
      return this.record.state === 'granted_waiting_price_acceptation'
    }
  },
  watch: {
    windowWidth(newWidth, oldWidth) {
      this.width = newWidth < 768 ? '100%' : '50%'
    }
  },
  async mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.windowWidth = window.innerWidth
      })
    })
    // init
    this.windowWidth = window.innerWidth

    try {
      this.loading = true

      this.record = await this.$api.records.getRecord({ id: this.recordId })
      this.optionalChoices = this.record.residence_services.map(service => ({
        id: service.id,
        title: service.title,
        description: service.description,
        price: service.price
      }))
    } catch (error) {
      console.warn(error)
    }

    this.loading = false
  },
  methods: {
    handleClose() {
      this.$emit('close')
    },
    async handleUpdate() {
      try {
        this.loading = true
        const updated = await this.$store.dispatch('records/updateRecord', {
          record: {
            state: this.isWaitingPrice ?
              this.mapStates[this.record.state][this.radio] :
              this.mapStates[this.record.state],
            motive: (this.isWaitingPrice && !this.radio) ? this.radioChoice : undefined,
            residence_service_ids: Object.keys(this.checkboxChoice).filter(choice => this.checkboxChoice[choice]) || [],
          },
          id: this.record.id
        })
        await this.$notify({
          type: 'success',
          title: this.$t('base.success.notificationTitle'),
          message: this.$t('records.dialog.notification') + updated.state_label
        })
        this.radio = null
        this.$emit('update', updated)
        this.$emit('close')
      } catch (error) {
        this.loading = false
      }
    },
    onRadioChange() {
      if (this.radio) {
        // accept 
        this.radioChoice = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$c: '.recordDialog';

#{$c} {
  &__radio {
    margin-top: 37px;
  }

  &__radioGroup {
    display: grid;
  }
  &__radioGroup .el-radio:first-child {
    margin-bottom: 20px;
  }
  &__select {
    margin-left: 40px;

    @include respond-below(xs) {
      display: flex;
      margin-top: 10px;
    }
  }
  &__infos {
    border-left: 2px $--color-primary solid;
    padding-left: 20px;
    margin-left: 0;
  }

  &__optional {
    margin-top: 14px;
    margin-bottom: 40px;

    .primaryColor {
      color: $--color-primary;
      font-size: 18px;
      word-break: break-word;
    }
    &_text {
      color: $--color-info;
      margin-top: 14px;
      margin-bottom: 14px;
      white-space: normal;
      word-break: break-word;
    }
    &_checkbox {
      label {
        margin-right: 10px;
      }
      .el-button {
        border: 0;
        padding: 1px;
      }
      .info {
        font-size: 14px;
      }
    }
  }
}
</style>